import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Clock, Award, MapPin, ArrowRight } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Community First',
      description: 'We believe in giving back and being an active part of the Visalia community.',
    },
    {
      icon: Users,
      title: 'Friendly Service',
      description: 'Every customer is greeted with a smile and treated like family.',
    },
    {
      icon: Clock,
      title: 'Always Convenient',
      description: 'Open early and late to serve your needs whenever they arise.',
    },
    {
      icon: Award,
      title: 'Quality Products',
      description: 'We stock only the best brands and freshest products for you.',
    },
  ];

  return (
    <div className="grain-bg" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#1A4D2E]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up">
              About Shah Market
            </h1>
            <p className="text-xl text-white/80 animate-fade-in-up animation-delay-100">
              More than just a store – we're your neighbors, serving the Visalia community with care and dedication.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <span className="text-[#E07A5F] font-semibold uppercase tracking-wider text-sm">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B] mt-2 mb-6">
                A Family-Run Store with Heart
              </h2>
              <div className="space-y-4 text-[#5F6C7B] text-lg leading-relaxed">
                <p>
                  Shah Market started with a simple dream – to create a welcoming place where neighbors could find everything they need without the hassle of a big supermarket.
                </p>
                <p>
                  We take pride in knowing our customers by name, remembering their preferences, and going the extra mile to make sure every visit is a pleasant one.
                </p>
                <p>
                  From fresh groceries and cold beverages to essential services like ATM and bill payment, we've grown to meet the diverse needs of our community while keeping that small-town feel.
                </p>
              </div>
              <Link
                to="/location"
                className="btn-primary px-6 py-3 rounded-xl font-medium inline-flex items-center gap-2 mt-8"
                data-testid="visit-us-button"
              >
                Visit Us Today
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_website-build-11/artifacts/6m4ot12p_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%284%29.jpeg"
                  alt="Inside Shah Market store"
                  className="rounded-2xl shadow-lg w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden md:block">
                  <p className="font-bold text-[#1A4D2E] text-2xl">Years of</p>
                  <p className="text-[#5F6C7B]">Serving Visalia</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#E07A5F] font-semibold uppercase tracking-wider text-sm">
              What We Stand For
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B] mt-2">
              Our Values
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-[#F4F1DE] rounded-2xl p-6 text-center card-hover"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#1A4D2E] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon size={28} className="text-white" />
                </div>
                <h3 className="font-bold text-[#3D405B] text-lg mb-2">{value.title}</h3>
                <p className="text-[#5F6C7B]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-[#1A4D2E] rounded-3xl p-8 md:p-12 lg:p-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-[#E07A5F] font-semibold uppercase tracking-wider text-sm">
                  Our Mission
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
                  Making Every Day Easier
                </h2>
                <p className="text-white/80 text-lg leading-relaxed">
                  Our mission is simple: to be the most convenient and welcoming store in Visalia. We want to save you time, offer fair prices, and make sure you always leave with a smile.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-2xl p-6 text-center">
                  <p className="text-4xl font-bold text-white mb-1">7</p>
                  <p className="text-white/70 text-sm">Days a Week</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 text-center">
                  <p className="text-4xl font-bold text-white mb-1">100+</p>
                  <p className="text-white/70 text-sm">Products</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 text-center">
                  <p className="text-4xl font-bold text-white mb-1">1000+</p>
                  <p className="text-white/70 text-sm">Happy Customers</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 text-center">
                  <p className="text-4xl font-bold text-white mb-1">17+</p>
                  <p className="text-white/70 text-sm">Hours Daily</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#E07A5F]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            We'd Love to See You
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Stop by anytime – we're always happy to help!
          </p>
          <Link
            to="/location"
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 text-lg hover:bg-white/90 transition-all"
            data-testid="about-cta-button"
          >
            <MapPin size={20} />
            Find Our Location
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
